import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Loader2, X, Camera, FileDown } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { WatchlistSubNav } from '@/components/watchlist/SubNav';
import { Modal, FieldLabel, TextInput, TextArea, PrimaryButton, GhostButton } from '@/components/watchlist/Modal';
import { BlobImage } from '@/components/watchlist/BlobImage';
import { wl, severityColor } from '@/components/watchlist/theme';
import type { TakedownCaseRecord, TakedownStatus, JurisdictionComplianceRef } from '@/types/watchlist';

const STATUS_COLUMNS: { status: TakedownStatus; label: string; color: string }[] = [
  { status: 'new', label: 'New', color: wl.textMuted },
  { status: 'reported', label: 'Reported', color: wl.info },
  { status: 'acknowledged', label: 'Acknowledged', color: wl.medium },
  { status: 'removed', label: 'Removed', color: wl.green },
  { status: 'rejected', label: 'Rejected', color: wl.critical },
  { status: 'escalated', label: 'Escalated', color: wl.high },
];

interface CreateFormState {
  title: string;
  description: string;
  profileType: 'brand' | 'vip';
  profileId: string;
  targetPlatform: string;
  targetHandle: string;
  targetUrl: string;
  classification: string;
  severity: string;
}

function CreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const brandsQuery = useQuery({ queryKey: ['watchlist-brand-profiles'], queryFn: () => watchlistApi.listBrandProfiles() });
  const vipsQuery = useQuery({ queryKey: ['watchlist-vip-profiles'], queryFn: () => watchlistApi.listVipProfiles() });
  const [form, setForm] = useState<CreateFormState>({
    title: '', description: '', profileType: 'brand', profileId: '',
    targetPlatform: '', targetHandle: '', targetUrl: '', classification: '', severity: 'medium',
  });
  const [refs, setRefs] = useState<JurisdictionComplianceRef[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profileOptions = form.profileType === 'brand' ? (brandsQuery.data?.brand_profiles ?? []) : (vipsQuery.data?.vip_profiles ?? []);

  async function handleSave() {
    if (!form.title.trim() || !form.profileId || !form.targetPlatform.trim()) {
      setError('Title, profile, and target platform are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await watchlistApi.createTakedownCase({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        profile_type: form.profileType,
        profile_id: form.profileId,
        target_platform: form.targetPlatform.trim(),
        target_handle: form.targetHandle.trim() || undefined,
        target_url: form.targetUrl.trim() || undefined,
        classification: form.classification || undefined,
        severity: form.severity,
        status: 'new',
        evidence_attachments: [],
        jurisdiction_compliance_refs: refs.filter((r) => r.jurisdiction_code && r.authority_name && r.reference_url_or_contact),
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="New Takedown Case" onClose={onClose} maxWidth={620} accent={wl.high}>
      <div className="flex flex-col gap-4">
        <div>
          <FieldLabel>Title</FieldLabel>
          <TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Fake support account on Telegram" />
        </div>
        <div>
          <FieldLabel>Description</FieldLabel>
          <TextArea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <FieldLabel>Profile type</FieldLabel>
            <select
              value={form.profileType}
              onChange={(e) => setForm((f) => ({ ...f, profileType: e.target.value as 'brand' | 'vip', profileId: '' }))}
              style={{ background: wl.bg, border: `1.5px solid ${wl.border}`, color: wl.text, fontSize: 13, borderRadius: 6, padding: '9px 12px', width: '100%', fontFamily: wl.font }}
            >
              <option value="brand">Brand</option>
              <option value="vip">VIP</option>
            </select>
          </div>
          <div className="flex-1">
            <FieldLabel>Profile</FieldLabel>
            <select
              value={form.profileId}
              onChange={(e) => setForm((f) => ({ ...f, profileId: e.target.value }))}
              style={{ background: wl.bg, border: `1.5px solid ${wl.border}`, color: wl.text, fontSize: 13, borderRadius: 6, padding: '9px 12px', width: '100%', fontFamily: wl.font }}
            >
              <option value="">Select…</option>
              {profileOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <FieldLabel>Target platform</FieldLabel>
            <TextInput value={form.targetPlatform} onChange={(e) => setForm((f) => ({ ...f, targetPlatform: e.target.value }))} placeholder="telegram" />
          </div>
          <div className="flex-1">
            <FieldLabel>Target handle</FieldLabel>
            <TextInput value={form.targetHandle} onChange={(e) => setForm((f) => ({ ...f, targetHandle: e.target.value }))} />
          </div>
        </div>
        <div>
          <FieldLabel>Target URL</FieldLabel>
          <TextInput value={form.targetUrl} onChange={(e) => setForm((f) => ({ ...f, targetUrl: e.target.value }))} />
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <FieldLabel>Classification</FieldLabel>
            <TextInput value={form.classification} onChange={(e) => setForm((f) => ({ ...f, classification: e.target.value }))} placeholder="impersonation" />
          </div>
          <div className="flex-1">
            <FieldLabel>Severity</FieldLabel>
            <select
              value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              style={{ background: wl.bg, border: `1.5px solid ${wl.border}`, color: wl.text, fontSize: 13, borderRadius: 6, padding: '9px 12px', width: '100%', fontFamily: wl.font }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div>
          <FieldLabel>Jurisdiction / compliance references</FieldLabel>
          <div className="flex flex-col gap-2">
            {refs.map((r, i) => (
              <div key={i} className="flex gap-2 items-center">
                <TextInput style={{ width: 60 }} value={r.jurisdiction_code} onChange={(e) => setRefs((rs) => rs.map((x, idx) => idx === i ? { ...x, jurisdiction_code: e.target.value } : x))} placeholder="EU" />
                <TextInput style={{ flex: 1 }} value={r.authority_name} onChange={(e) => setRefs((rs) => rs.map((x, idx) => idx === i ? { ...x, authority_name: e.target.value } : x))} placeholder="Authority name" />
                <TextInput style={{ flex: 1 }} value={r.reference_url_or_contact} onChange={(e) => setRefs((rs) => rs.map((x, idx) => idx === i ? { ...x, reference_url_or_contact: e.target.value } : x))} placeholder="URL or contact" />
                <button onClick={() => setRefs((rs) => rs.filter((_, idx) => idx !== i))} style={{ color: wl.textMuted }}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setRefs((rs) => [...rs, { jurisdiction_code: '', authority_name: '', reference_url_or_contact: '' }])}
            className="flex items-center gap-1.5 mt-2"
            style={{ color: wl.high, fontSize: 12 }}
          >
            <Plus size={13} /> Add reference
          </button>
        </div>

        {error && <p style={{ color: wl.critical, fontSize: 12 }}>{error}</p>}

        <div className="flex justify-end gap-2 mt-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Create case'}</PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

function DetailModal({ caseRecord, onClose, onChanged }: { caseRecord: TakedownCaseRecord; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [notes, setNotes] = useState(caseRecord.investigation_notes ?? '');
  const [resolutionNotes, setResolutionNotes] = useState(caseRecord.resolution_notes ?? '');
  const [assignee, setAssignee] = useState(caseRecord.assignee ?? '');
  const [capturing, setCapturing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  const statusMut = useMutation({
    mutationFn: (status: TakedownStatus) => watchlistApi.updateTakedownCase(caseRecord.id, { status }),
    onSuccess: () => { onChanged(); qc.invalidateQueries({ queryKey: ['watchlist-takedown-cases'] }); },
  });
  const fieldsMut = useMutation({
    mutationFn: () => watchlistApi.updateTakedownCase(caseRecord.id, { investigation_notes: notes, resolution_notes: resolutionNotes, assignee: assignee || undefined }),
    onSuccess: () => onChanged(),
  });
  const evidenceMut = useMutation({
    mutationFn: () => watchlistApi.addTakedownEvidence(caseRecord.id, { attachment_type: 'url_reference', url_or_asset_id: evidenceUrl.trim() }),
    onSuccess: () => { setEvidenceUrl(''); onChanged(); },
  });

  async function captureScreenshot() {
    if (!screenshotUrl.trim()) return;
    setCapturing(true);
    setCaptureError(null);
    try {
      await watchlistApi.captureTakedownScreenshot(caseRecord.id, screenshotUrl.trim());
      setScreenshotUrl('');
      onChanged();
    } catch (e) {
      setCaptureError(e instanceof Error ? e.message : 'Capture failed');
    } finally {
      setCapturing(false);
    }
  }

  async function downloadPdf() {
    setDownloadingPdf(true);
    try {
      const blob = await watchlistApi.downloadTakedownEvidencePdf(caseRecord.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `takedown-${caseRecord.id}-evidence.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingPdf(false);
    }
  }

  const screenshots = caseRecord.evidence_attachments.filter((e) => e.attachment_type === 'screenshot');
  const otherEvidence = caseRecord.evidence_attachments.filter((e) => e.attachment_type !== 'screenshot');

  return (
    <Modal title={caseRecord.title} onClose={onClose} maxWidth={680} accent={wl.high}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div><span style={{ color: wl.textFaint, fontSize: 11 }}>Target</span><p style={{ color: wl.text, fontSize: 13 }}>{caseRecord.target_platform} {caseRecord.target_handle ? `· @${caseRecord.target_handle}` : ''}</p></div>
          <div><span style={{ color: wl.textFaint, fontSize: 11 }}>Severity</span><p style={{ color: severityColor(caseRecord.severity), fontSize: 13, textTransform: 'capitalize' }}>{caseRecord.severity}</p></div>
          <div><span style={{ color: wl.textFaint, fontSize: 11 }}>Classification</span><p style={{ color: wl.text, fontSize: 13 }}>{caseRecord.classification ?? '—'}</p></div>
        </div>

        <div>
          <FieldLabel>Status</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {STATUS_COLUMNS.map((col) => (
              <button
                key={col.status}
                onClick={() => statusMut.mutate(col.status)}
                className="px-3 py-1.5 rounded-lg font-semibold"
                style={{
                  background: caseRecord.status === col.status ? `${col.color}24` : 'transparent',
                  color: caseRecord.status === col.status ? col.color : wl.textMuted,
                  border: `1px solid ${caseRecord.status === col.status ? col.color + '50' : wl.border}`,
                  fontSize: 11.5,
                }}
              >
                {col.label}
              </button>
            ))}
          </div>
          {caseRecord.status_history.length > 0 && (
            <div className="mt-2 flex flex-col gap-1">
              {caseRecord.status_history.slice().reverse().map((h, i) => (
                <p key={i} style={{ color: wl.textFaint, fontSize: 10.5 }}>{h.status} · {new Date(h.changed_at).toLocaleString()}</p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <FieldLabel>Assignee</FieldLabel>
            <TextInput value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="unassigned" />
          </div>
        </div>

        {caseRecord.jurisdiction_compliance_refs.length > 0 && (
          <div>
            <FieldLabel>Jurisdiction references</FieldLabel>
            <div className="flex flex-col gap-1.5">
              {caseRecord.jurisdiction_compliance_refs.map((r, i) => (
                <p key={i} style={{ color: wl.textMuted, fontSize: 12 }}>
                  <strong style={{ color: wl.text }}>{r.jurisdiction_code}</strong> — {r.authority_name} ({r.reference_url_or_contact})
                </p>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <FieldLabel>Evidence</FieldLabel>
            <GhostButton onClick={downloadPdf}><FileDown size={12} className="inline mr-1" />{downloadingPdf ? 'Building…' : 'Download evidence PDF'}</GhostButton>
          </div>
          {screenshots.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {screenshots.map((e, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <BlobImage
                    path={`/watchlist/takedown-cases/${caseRecord.id}/evidence/screenshot/${e.url_or_asset_id}/image`}
                    style={{ width: 90, height: 64, objectFit: 'cover', borderRadius: 6, border: `1px solid ${wl.border}` }}
                  />
                  <span style={{ color: wl.textFaint, fontSize: 9 }}>{e.sha256_hash?.slice(0, 10)}…</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col gap-1.5 mb-2">
            {otherEvidence.map((e, i) => (
              <p key={i} style={{ color: wl.textMuted, fontSize: 12 }}>· {e.url_or_asset_id}</p>
            ))}
            {caseRecord.evidence_attachments.length === 0 && <p style={{ color: wl.textFaint, fontSize: 12 }}>No evidence attached yet.</p>}
          </div>
          <div className="flex gap-2 mb-2">
            <TextInput style={{ flex: 1 }} value={screenshotUrl} onChange={(e) => setScreenshotUrl(e.target.value)} placeholder="URL to capture a real screenshot of" />
            <GhostButton onClick={captureScreenshot}><Camera size={12} className="inline mr-1" />{capturing ? 'Capturing…' : 'Capture screenshot'}</GhostButton>
          </div>
          {captureError && <p style={{ color: wl.critical, fontSize: 11.5, marginBottom: 6 }}>{captureError}</p>}
          <div className="flex gap-2">
            <TextInput style={{ flex: 1 }} value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} placeholder="Evidence URL or reference" />
            <GhostButton onClick={() => evidenceUrl.trim() && evidenceMut.mutate()}>Add</GhostButton>
          </div>
        </div>

        <div>
          <FieldLabel>Investigation notes</FieldLabel>
          <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div>
          <FieldLabel>Resolution notes</FieldLabel>
          <TextArea value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <GhostButton onClick={() => fieldsMut.mutate()}>Save fields</GhostButton>
          <GhostButton onClick={onClose}>Close</GhostButton>
        </div>
      </div>
    </Modal>
  );
}

export default function TakedownsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<TakedownCaseRecord | null>(null);

  const query = useQuery({ queryKey: ['watchlist-takedown-cases'], queryFn: () => watchlistApi.listTakedownCases() });

  const deleteMut = useMutation({
    mutationFn: (id: string) => watchlistApi.deleteTakedownCase(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['watchlist-takedown-cases'] }); setSelected(null); },
  });

  function refetch() {
    qc.invalidateQueries({ queryKey: ['watchlist-takedown-cases'] });
  }

  const cases = query.data?.cases ?? [];
  const stats = useMemo(() => {
    const thirtyDaysAgo = Date.now() - 30 * 86400000;
    const open = cases.filter((c) => !['removed', 'rejected'].includes(c.status)).length;
    const removed30d = cases.filter((c) => c.status === 'removed' && new Date(c.updated_at).getTime() >= thirtyDaysAgo).length;
    const rejected = cases.filter((c) => c.status === 'rejected').length;
    const removalTimes = cases
      .filter((c) => c.status === 'removed')
      .map((c) => new Date(c.updated_at).getTime() - new Date(c.created_at).getTime())
      .sort((a, b) => a - b);
    const medianMs = removalTimes.length ? removalTimes[Math.floor(removalTimes.length / 2)] : null;
    return { open, removed30d, rejected, medianHours: medianMs != null ? Math.round(medianMs / 3600000) : null };
  }, [cases]);

  // Keep the active selected case in sync with refetched data (status/evidence changes).
  const selectedFresh = selected ? cases.find((c) => c.id === selected.id) ?? selected : null;

  return (
    <div style={{ background: wl.bg, minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WatchlistSubNav />
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSaved={refetch} />}
      {selectedFresh && <DetailModal caseRecord={selectedFresh} onClose={() => setSelected(null)} onChanged={() => { refetch(); }} />}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 style={{ color: wl.text, fontWeight: 800, fontSize: 16 }}>Takedowns</h1>
          <p style={{ color: wl.textMuted, fontSize: 12, marginTop: 2 }}>Case workflow, evidence, and jurisdiction compliance references</p>
        </div>
        <PrimaryButton onClick={() => setShowCreate(true)}>+ New case</PrimaryButton>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Open cases', value: stats.open },
          { label: 'Removed · 30 days', value: stats.removed30d },
          { label: 'Median time to removal', value: stats.medianHours != null ? `${stats.medianHours}h` : '—' },
          { label: 'Rejected by platform', value: stats.rejected },
        ].map((s) => (
          <div key={s.label} style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <p style={{ color: wl.text, fontWeight: 800, fontSize: 22 }}>{s.value}</p>
            <p style={{ color: wl.textMuted, fontSize: 11, marginTop: 2 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {query.isLoading ? (
        <Loader2 className="animate-spin" size={22} style={{ color: wl.high }} />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {STATUS_COLUMNS.map((col) => {
            const colCases = cases.filter((c) => c.status === col.status);
            return (
              <div key={col.status} style={{ minWidth: 260, flexShrink: 0 }}>
                <div className="flex items-center gap-2 mb-3">
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: col.color }} />
                  <span style={{ color: wl.text, fontSize: 12.5, fontWeight: 700 }}>{col.label}</span>
                  <span style={{ color: wl.textFaint, fontSize: 11.5 }}>{colCases.length}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {colCases.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => setSelected(c)}
                      className="cursor-pointer"
                      style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 8, padding: '12px 14px' }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p style={{ color: wl.text, fontSize: 12.5, fontWeight: 600 }}>{c.title}</p>
                        <button onClick={(e) => { e.stopPropagation(); confirm('Delete this case?') && deleteMut.mutate(c.id); }} style={{ color: wl.textMuted, flexShrink: 0 }}>
                          <X size={13} />
                        </button>
                      </div>
                      <p style={{ color: wl.textMuted, fontSize: 11, marginTop: 4 }}>{c.target_platform}{c.target_handle ? ` · @${c.target_handle}` : ''}</p>
                      {c.assignee && <p style={{ color: wl.textFaint, fontSize: 10.5, marginTop: 2 }}>assigned: {c.assignee}</p>}
                    </div>
                  ))}
                  {colCases.length === 0 && <p style={{ color: wl.textFaint, fontSize: 11.5 }}>Empty</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
